import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Team data structure from API
 */
export interface Team {
  id: string
  name: string
  image?: string | null
  gamesCount: number
  schedulerTeamNotes?: string | null
}

/**
 * Division data structure for the UI
 */
export interface DivisionItem {
  id: string
  name: string
  subtitle?: string
  lastUpdate?: string
  teamsCount: number
  playersCount: number
  isApproved: boolean
  isExpanded?: boolean
  teams: Team[]
  divisionGroup?: string | null
  gamesCount: number
  isReady: boolean
  readyAt?: string | null
  readyByUserName?: string | null
  lastModifiedAt?: string | null
  lastModifiedByUserName?: string | null
}

/**
 * API response structure for schedule children (SimpleScheduleResource)
 */
interface ScheduleChild {
  id: string
  name: string
  name_full?: string
  starts_at?: string
  ends_at?: string
  division_group?: string | null
  is_scheduler_ready?: boolean
  scheduler_ready_at?: string | null
  scheduler_ready_by_user_name?: string | null
  scheduler_last_modified_at?: string | null
  scheduler_last_modified_by_user_name?: string | null
  teams?: Array<{
    id: string
    name: string
    is_placeholder?: boolean
    image?: string | null
    seed?: number
    games_count?: number
    scheduler_team_notes?: string | null
  }> | null
}

/**
 * API response structure for schedule with children
 * Note: children comes wrapped in a 'data' property from SimpleScheduleCollection
 */
interface ScheduleWithChildren {
  id: string
  children?: {
    data: ScheduleChild[]
  }
}

/**
 * Hook result interface
 */
interface UseSchedulerDivisionsResult {
  /** List of divisions with teams */
  divisions: DivisionItem[]
  /** Loading state */
  loading: boolean
  /** Error state */
  error: Error | null
  /** Refetch divisions */
  refetch: () => Promise<void>
  /** Update a division's expanded state */
  toggleExpanded: (divisionId: string) => void
  /** Add a team to a division */
  addTeam: (divisionId: string, teamName: string) => Promise<void>
  /** Add a division to the competition */
  addDivision: (
    orgId: string,
    competitionId: string,
    divisionName: string,
    startsAt: string,
    endsAt: string
  ) => Promise<void>
  /** Delete a team from a division */
  deleteTeam: (divisionId: string, teamId: string) => Promise<void>
  /** Delete a division (and optionally force delete with children) */
  deleteDivision: (divisionId: string, force?: boolean) => Promise<void>
  /** Toggle scheduler ready status for a division */
  toggleDivisionReady: (divisionId: string) => Promise<void>
  /** Bulk set ready/unready for multiple divisions. Returns count of updated divisions. */
  bulkSetReady: (divisionIds: string[], isReady: boolean) => Promise<number>
  /** Update team notes within a division */
  updateTeamNotes: (
    divisionId: string,
    teamId: string,
    notes: string
  ) => Promise<void>
  /** Divisions grouped by conference/division_group */
  divisionsByConference: Map<string, DivisionItem[]>
  /** Percentage of divisions marked as ready */
  readyPercentage: number
  /** Count of ready divisions */
  readyCount: number
}

/**
 * Map API response to DivisionItem
 */
const mapChildToDivision = (child: ScheduleChild): DivisionItem => {
  const teamsArray = child.teams || []
  const teams: Team[] = teamsArray.map((team) => ({
    id: team.id,
    name: team.name || 'Unknown Team',
    image: team.image || null,
    gamesCount: team.games_count || 0,
    schedulerTeamNotes: team.scheduler_team_notes || null,
  }))

  return {
    id: child.id,
    name: child.name || child.name_full || 'Unknown Division',
    subtitle: 'Schedule Pending',
    lastUpdate: undefined,
    teamsCount: teams.length,
    playersCount: 0,
    isApproved: false,
    isExpanded: false,
    teams,
    divisionGroup: child.division_group || null,
    gamesCount: 0,
    isReady: child.is_scheduler_ready || false,
    readyAt: child.scheduler_ready_at || null,
    readyByUserName: child.scheduler_ready_by_user_name || null,
    lastModifiedAt: child.scheduler_last_modified_at || null,
    lastModifiedByUserName: child.scheduler_last_modified_by_user_name || null,
  }
}

/**
 * Hook for fetching divisions and teams for the scheduler wizard
 * @param competitionId - The competition/schedule ID to fetch divisions for
 * @returns Object containing divisions data, loading states, and helper functions
 */
export const useSchedulerDivisions = (
  competitionId?: string,
  approvedDivisionUids?: string[],
  flightId?: string | null
): UseSchedulerDivisionsResult => {
  const [divisions, setDivisions] = useState<DivisionItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const approvedUidsRef = useRef(approvedDivisionUids)

  /**
   * Fetches divisions and teams for the competition
   * Preserves UI state (isExpanded, isLocked) across refetches
   */
  const fetchDivisions = useCallback(async () => {
    if (!competitionId) {
      setDivisions([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await req(`/schedules/${competitionId}`, {
        query: {
          children: true,
          ...(flightId ? { flight_id: flightId } : {}),
        },
      })

      const scheduleData = data as ScheduleWithChildren
      const childrenArray = scheduleData.children?.data || []
      const mappedDivisions = childrenArray.map(mapChildToDivision)

      // Preserve UI state (isExpanded, isApproved) from previous divisions
      // On first load, seed isApproved from approvedDivisionUids
      const approvedSet = new Set(approvedUidsRef.current || [])
      setDivisions((prevDivisions) => {
        // Create a map of previous UI states by division ID
        const prevStateMap = new Map(
          prevDivisions.map((div) => [
            div.id,
            { isExpanded: div.isExpanded, isApproved: div.isApproved },
          ])
        )

        // Apply previous UI state to new divisions
        return mappedDivisions.map((div) => {
          const prevState = prevStateMap.get(div.id)
          if (prevState) {
            return {
              ...div,
              isExpanded: prevState.isExpanded,
              isApproved: prevState.isApproved,
            }
          }
          // First load: seed from approvedDivisionUids
          return {
            ...div,
            isApproved: approvedSet.has(div.id),
            subtitle: approvedSet.has(div.id) ? 'Published' : div.subtitle,
          }
        })
      })
    } catch (err) {
      const errorObj =
        err instanceof Error ? err : new Error('Failed to fetch divisions')
      setError(errorObj)
      setDivisions([])
    } finally {
      setLoading(false)
    }
  }, [competitionId, flightId])

  /**
   * Toggle expanded state for a division
   */
  const toggleExpanded = useCallback((divisionId: string) => {
    setDivisions((prev) =>
      prev.map((div) =>
        div.id === divisionId ? { ...div, isExpanded: !div.isExpanded } : div
      )
    )
  }, [])

  /**
   * Toggle scheduler ready status for a division
   */
  const toggleDivisionReady = useCallback(async (divisionId: string) => {
    try {
      const { data } = await req(
        `/scheduler/schedules/${divisionId}/toggle-ready`,
        { method: 'POST' }
      )
      const result = data as {
        is_scheduler_ready: boolean
        scheduler_ready_at?: string | null
        scheduler_ready_by_user_name?: string | null
        scheduler_last_modified_at?: string | null
        scheduler_last_modified_by_user_name?: string | null
      }
      setDivisions((prev) =>
        prev.map((div) =>
          div.id === divisionId
            ? {
                ...div,
                isReady: result.is_scheduler_ready,
                readyAt: result.scheduler_ready_at || null,
                readyByUserName: result.scheduler_ready_by_user_name || null,
                lastModifiedAt: result.scheduler_last_modified_at || null,
                lastModifiedByUserName:
                  result.scheduler_last_modified_by_user_name || null,
              }
            : div
        )
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to toggle ready status'
      throw new Error(errorMessage)
    }
  }, [])

  /**
   * Bulk set ready/unready for multiple divisions
   */
  const bulkSetReady = useCallback(
    async (divisionIds: string[], isReady: boolean): Promise<number> => {
      try {
        const { data } = await req('/scheduler/schedules/bulk-toggle-ready', {
          method: 'POST',
          body: JSON.stringify({
            division_uids: divisionIds,
            is_ready: isReady,
          }),
        })
        const results = data as Array<{
          id: string
          is_scheduler_ready: boolean
          scheduler_ready_at?: string | null
          scheduler_ready_by_user_name?: string | null
          scheduler_last_modified_at?: string | null
          scheduler_last_modified_by_user_name?: string | null
        }>
        const resultMap = new Map(results.map((r) => [r.id, r]))
        setDivisions((prev) =>
          prev.map((div) => {
            const result = resultMap.get(div.id)
            if (result) {
              return {
                ...div,
                isReady: result.is_scheduler_ready,
                readyAt: result.scheduler_ready_at || null,
                readyByUserName: result.scheduler_ready_by_user_name || null,
                lastModifiedAt: result.scheduler_last_modified_at || null,
                lastModifiedByUserName:
                  result.scheduler_last_modified_by_user_name || null,
              }
            }
            return div
          })
        )
        return results.length
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to bulk update ready status'
        throw new Error(errorMessage)
      }
    },
    []
  )

  /**
   * Add a team to a division
   * @param divisionId - The division ID to add the team to
   * @param teamName - The name of the new team
   */
  const addTeam = useCallback(
    async (divisionId: string, teamName: string) => {
      try {
        await req(`/schedules/${divisionId}/teams`, {
          method: 'POST',
          body: JSON.stringify({ name: teamName }),
        })
        await fetchDivisions()
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to add team'
        throw new Error(errorMessage)
      }
    },
    [fetchDivisions]
  )

  /**
   * Add a division to the competition
   * @param orgId - The organization ID
   * @param parentCompetitionId - The parent competition ID
   * @param divisionName - The name of the new division
   * @param startsAt - Start date from the parent competition
   * @param endsAt - End date from the parent competition
   */
  const addDivision = useCallback(
    async (
      orgId: string,
      parentCompetitionId: string,
      divisionName: string,
      startsAt: string,
      endsAt: string
    ) => {
      try {
        await req(`/organizations/${orgId}/schedules`, {
          method: 'POST',
          body: JSON.stringify({
            parent_id: parentCompetitionId,
            name: divisionName,
            name_full: divisionName,
            type: 'Division',
            starts_at: startsAt,
            ends_at: endsAt,
          }),
        })
        await fetchDivisions()
      } catch (err) {
        // Extract error message from API response or use default
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to add division'
        throw new Error(errorMessage)
      }
    },
    [fetchDivisions]
  )

  /**
   * Delete a team from a division
   * @param divisionId - The division ID the team belongs to
   * @param teamId - The team ID to delete
   */
  const deleteTeam = useCallback(
    async (divisionId: string, teamId: string) => {
      try {
        await req(`/schedules/${divisionId}/teams/${teamId}`, {
          method: 'DELETE',
        })
        await fetchDivisions()
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to delete team'
        throw new Error(errorMessage)
      }
    },
    [fetchDivisions]
  )

  const updateTeamNotes = useCallback(
    async (divisionId: string, teamId: string, notes: string) => {
      try {
        await req(`/schedules/${divisionId}/teams/${teamId}/notes`, {
          method: 'PUT',
          body: JSON.stringify({ scheduler_team_notes: notes || null }),
        })
        setDivisions((prev) =>
          prev.map((div) =>
            div.id === divisionId
              ? {
                  ...div,
                  teams: div.teams.map((t) =>
                    t.id === teamId
                      ? { ...t, schedulerTeamNotes: notes || null }
                      : t
                  ),
                }
              : div
          )
        )
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update notes'
        throw new Error(errorMessage)
      }
    },
    []
  )

  /**
   * Delete a division
   * @param divisionId - The division ID to delete
   * @param force - If true, force delete even if division has children
   */
  const deleteDivision = useCallback(
    async (divisionId: string, force: boolean = false) => {
      try {
        const query = force ? { force: 1 } : {}
        await req(`/schedules/${divisionId}`, {
          method: 'DELETE',
          query,
        })
        await fetchDivisions()
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to delete division'
        throw new Error(errorMessage)
      }
    },
    [fetchDivisions]
  )

  // Computed: divisions grouped by conference + ready stats (single pass)
  const { divisionsByConference, readyCount, readyPercentage } = useMemo(() => {
    const map = new Map<string, DivisionItem[]>()
    let ready = 0
    divisions.forEach((div) => {
      const group = div.divisionGroup || 'GENERAL'
      const existing = map.get(group) || []
      existing.push(div)
      map.set(group, existing)
      if (div.isReady) {
        ready++
      }
    })
    return {
      divisionsByConference: map,
      readyCount: ready,
      readyPercentage:
        divisions.length > 0 ? (ready / divisions.length) * 100 : 0,
    }
  }, [divisions])

  // Fetch divisions when competitionId changes
  useEffect(() => {
    fetchDivisions()
  }, [fetchDivisions])

  // Keep ref in sync and update divisions when approvedDivisionUids changes from API
  useEffect(() => {
    approvedUidsRef.current = approvedDivisionUids
    if (!approvedDivisionUids) {
      return
    }
    const approvedSet = new Set(approvedDivisionUids)
    setDivisions((prev) => {
      if (prev.length === 0) {
        return prev
      }
      return prev.map((div) => ({
        ...div,
        isApproved: approvedSet.has(div.id),
        subtitle: approvedSet.has(div.id) ? 'Published' : 'Schedule Pending',
      }))
    })
  }, [approvedDivisionUids])

  return {
    divisions,
    loading,
    error,
    refetch: fetchDivisions,
    toggleExpanded,
    addTeam,
    addDivision,
    deleteTeam,
    deleteDivision,
    toggleDivisionReady,
    bulkSetReady,
    updateTeamNotes,
    divisionsByConference,
    readyPercentage,
    readyCount,
  }
}

export default useSchedulerDivisions
